/**
 * Doserne — Admin "Contracts" panel.
 *
 * Admin uploads a contract PDF (from PC/phone) and assigns it to a specific
 * member. Assigning attaches the PDF, resets any prior signature, and pushes
 * a notification to that member in THEIR chosen language. "Clear" empties a
 * member's contract again. Reachable from an admin-only link in Profile.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, FileText, ArrowLeft, UploadCloud, Trash2, CheckCircle2, Search, Bell, FilePlus, Link2, UserPlus,
  KeyRound, UserCheck, LogIn,
} from "lucide-react";
import { useDoserneAuth } from "./doserneAuth";
import { useLang } from "./i18n";
import DoserneShell from "./DoserneShell";
import DoserneGenerateModal from "./DoserneGenerateModal";
import PdfViewerModal from "./PdfViewerModal";
import LangFlag from "./LangFlag";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DICT = {
  ro: {
    title: "Contracte", subtitle: "Generează sau încarcă un contract și atribuie-l unui angajat. Primește notificare + link de semnat.",
    back: "Înapoi la profil", search: "Caută nume sau e-mail…", empty: "Niciun angajat.",
    assigned: "Contract atribuit", signed: "Semnat", none: "Fără contract",
    assign: "Încarcă PDF", replace: "Înlocuiește", clear: "Șterge", view: "Vezi PDF", generate: "Generează", new_employee: "Angajat nou", download_print: "Descarcă / Print", close: "Închide",
    uploading: "Se încarcă…", pushed: "Contract atribuit + notificare trimisă 📄", fail: "Eroare",
    cleared: "Contract șters", confirm_clear: "Ștergi contractul acestui angajat?",
    copy_link: "Copiază link", link_copied: "Link copiat!",
    gen_title: "Generează contract", gen_for: "Pentru", doc_type: "Tip document",
    doc_employment: "Contract de muncă", doc_overtime: "Acord de overtime", language: "Limbă",
    f_position: "Poziție", f_workplace: "Loc de muncă", f_start: "Data începerii", f_rate: "Salariu / tarif",
    f_from: "De la", f_to: "Până la", f_reason: "Motiv / descriere", f_notes: "Alte clauze",
    gen_btn: "Generează & trimite", generating: "Se generează…", gen_ok: "Contract generat + notificare trimisă 📄", cancel: "Anulează",
    reset_pw: "Resetează parola", reset_confirm: "Trimiți un link de resetare a parolei către",
    reset_sent: "Link de resetare trimis 🔑", reset_fail: "Nu s-a putut trimite",
    onb_created: "Cont creat", onb_login: "Autentificat", onb_login_pending: "Neautentificat", onb_signed: "Contract semnat", onb_signed_pending: "Nesemnat",
  },
  no: {
    title: "Kontrakter", subtitle: "Generer eller last opp en kontrakt og tildel den til en ansatt. De får varsel + signeringslenke.",
    back: "Tilbake til profil", search: "Søk navn eller e-post…", empty: "Ingen ansatte.",
    assigned: "Kontrakt tildelt", signed: "Signert", none: "Ingen kontrakt",
    assign: "Last opp PDF", replace: "Erstatt", clear: "Slett", view: "Se PDF", generate: "Generer", new_employee: "Ny ansatt", download_print: "Last ned / Skriv ut", close: "Lukk",
    uploading: "Laster opp…", pushed: "Kontrakt tildelt + varsel sendt 📄", fail: "Feil",
    cleared: "Kontrakt slettet", confirm_clear: "Slette kontrakten til denne ansatte?",
    copy_link: "Kopier lenke", link_copied: "Lenke kopiert!",
    gen_title: "Generer kontrakt", gen_for: "For", doc_type: "Dokumenttype",
    doc_employment: "Arbeidsavtale", doc_overtime: "Avtale om overtid", language: "Språk",
    f_position: "Stilling", f_workplace: "Arbeidssted", f_start: "Startdato", f_rate: "Lønn / sats",
    f_from: "Fra", f_to: "Til", f_reason: "Formål / beskrivelse", f_notes: "Øvrige vilkår",
    gen_btn: "Generer & send", generating: "Genererer…", gen_ok: "Kontrakt generert + varsel sendt 📄", cancel: "Avbryt",
    reset_pw: "Tilbakestill passord", reset_confirm: "Sende en lenke for tilbakestilling av passord til",
    reset_sent: "Tilbakestillingslenke sendt 🔑", reset_fail: "Kunne ikke sende",
    onb_created: "Konto opprettet", onb_login: "Logget inn", onb_login_pending: "Ikke logget inn", onb_signed: "Kontrakt signert", onb_signed_pending: "Ikke signert",
  },
  en: {
    title: "Contracts", subtitle: "Generate or upload a contract and assign it to an employee. They get a notification + signing link.",
    back: "Back to profile", search: "Search name or email…", empty: "No employees.",
    assigned: "Contract assigned", signed: "Signed", none: "No contract",
    assign: "Upload PDF", replace: "Replace", clear: "Clear", view: "View PDF", generate: "Generate", new_employee: "New employee", download_print: "Download / Print", close: "Close",
    uploading: "Uploading…", pushed: "Contract assigned + notification sent 📄", fail: "Error",
    cleared: "Contract cleared", confirm_clear: "Clear this employee's contract?",
    copy_link: "Copy link", link_copied: "Link copied!",
    gen_title: "Generate contract", gen_for: "For", doc_type: "Document type",
    doc_employment: "Employment contract", doc_overtime: "Overtime agreement", language: "Language",
    f_position: "Position", f_workplace: "Workplace", f_start: "Start date", f_rate: "Salary / rate",
    f_from: "From", f_to: "To", f_reason: "Purpose / description", f_notes: "Other terms",
    gen_btn: "Generate & send", generating: "Generating…", gen_ok: "Contract generated + notification sent 📄", cancel: "Cancel",
    reset_pw: "Reset password", reset_confirm: "Send a password reset link to",
    reset_sent: "Reset link sent 🔑", reset_fail: "Could not send",
    onb_created: "Account created", onb_login: "Logged in", onb_login_pending: "Not logged in", onb_signed: "Contract signed", onb_signed_pending: "Not signed",
  },
};

export default function DoserneContracts() {
  const { user, loading, authHeader } = useDoserneAuth();
  const { lang } = useLang();
  const t = useMemo(() => DICT[lang] || DICT.no, [lang]);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [working, setWorking] = useState("");
  const [genMember, setGenMember] = useState(null);
  const [pdfView, setPdfView] = useState(null);
  const fileRefs = useRef({});

  const isAdmin = !!(user?.is_admin || user?.role === "platform_admin" || user?.is_doserne_admin);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/doserne/admin/contracts`, authHeader());
      setRows(data?.contracts || []);
    } catch { /* silent */ }
    setBusy(false);
  };
  useEffect(() => { if (user && isAdmin) load(); }, [user, isAdmin]);

  const onPick = async (email, file) => {
    if (!file) return;
    if (file.type !== "application/pdf") { toast.error("PDF"); return; }
    setWorking(email);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await axios.post(`${API}/doserne/admin/contracts/upload`, fd, authHeader());
      const url = up.data?.url;
      if (!url) throw new Error("no url");
      await axios.post(`${API}/doserne/admin/contracts/assign`, { email, contract_pdf_url: url }, authHeader());
      toast.success(t.pushed);
      await load();
    } catch { toast.error(t.fail); }
    setWorking("");
  };

  const clear = async (email) => {
    if (!window.confirm(t.confirm_clear)) return;
    setWorking(email);
    try {
      await axios.post(`${API}/doserne/admin/contracts/clear`, { email }, authHeader());
      toast.success(t.cleared);
      await load();
    } catch { toast.error(t.fail); }
    setWorking("");
  };

  const copyLink = async (r) => {
    if (!r.sign_token) return;
    const link = `${window.location.origin}/doserne/kontrakt/${r.sign_token}`;
    try { await navigator.clipboard.writeText(link); toast.success(t.link_copied); }
    catch { window.prompt("Link", link); }
  };

  const resetPassword = async (r) => {
    if (!window.confirm(`${t.reset_confirm} ${r.email}?`)) return;
    setWorking(r.email);
    try {
      await axios.post(`${API}/doserne/admin/users/send-reset`, { email: r.email }, authHeader());
      toast.success(t.reset_sent);
    } catch { toast.error(t.reset_fail); }
    setWorking("");
  };

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) => [r.full_name, r.email].some((v) => (v || "").toLowerCase().includes(s)));
  }, [rows, q]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#fafaf7]"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  if (!user) return <Navigate to="/doserne/logg-inn" replace />;
  if (!isAdmin) return <Navigate to="/doserne" replace />;

  return (
    <DoserneShell title={t.title}>
      <Link to="/doserne/profil" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 mb-3">
        <ArrowLeft className="w-3.5 h-3.5" /> {t.back}
      </Link>

      <div className="bg-gradient-to-br from-sky-50 via-white to-slate-50 border-2 border-sky-200 rounded-2xl p-4 mb-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-sky-600 flex items-center justify-center flex-shrink-0" style={{ color: "#fff" }}>
            <FileText className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-lg font-black text-slate-900">{t.title}</h1>
            <p className="text-sm text-slate-700 mt-0.5">{t.subtitle}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setGenMember({ __new: true })}
          className="mt-4 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-sm font-black"
          data-testid="new-employee-btn"
        >
          <UserPlus className="w-4 h-4" /> {t.new_employee || "Angajat nou"}
        </button>
      </div>

      <div className="relative mb-4">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t.search}
          className="w-full pl-9 pr-3 py-2.5 border border-slate-200 rounded-xl text-sm bg-white"
          data-testid="contracts-search"
        />
      </div>

      {busy && rows.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-4 text-sm text-slate-500">{t.empty}</div>
      ) : (
        <ul className="space-y-2">
          {filtered.map((r) => {
            const st = r.signed_at ? "signed" : r.has_contract ? "assigned" : "none";
            const isWorking = working === r.email;
            return (
              <li key={r.email} className="bg-white border border-slate-200 rounded-xl p-3" data-testid={`contract-row-${r.email}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-black text-slate-900 truncate">{r.full_name}</div>
                    <div className="text-[11px] text-slate-500 truncate">{r.email} · {r.lang?.toUpperCase()}</div>
                    <span className={`inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-full text-[10px] font-black ${
                      st === "signed" ? "bg-emerald-100 text-emerald-700"
                      : st === "assigned" ? "bg-amber-100 text-amber-700"
                      : "bg-slate-100 text-slate-500"}`}>
                      {st === "signed" ? <><CheckCircle2 className="w-3 h-3" /> {t.signed}</>
                        : st === "assigned" ? <><Bell className="w-3 h-3" /> {t.assigned}</>
                        : t.none}
                    </span>
                    {/* Onboarding state: account created → logged in → contract signed */}
                    <div className="flex items-center gap-1.5 mt-1.5 flex-wrap" data-testid={`onboarding-${r.email}`}>
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-sky-50 text-sky-700 border border-sky-100" title={r.account_created_at || ""}>
                        <UserCheck className="w-3 h-3" /> {t.onb_created}
                      </span>
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-bold border ${r.first_login_at ? "bg-emerald-50 text-emerald-700 border-emerald-100" : "bg-slate-50 text-slate-400 border-slate-100"}`} title={r.first_login_at || ""}>
                        <LogIn className="w-3 h-3" /> {r.first_login_at ? t.onb_login : t.onb_login_pending}
                      </span>
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-bold border ${r.signed_at ? "bg-emerald-50 text-emerald-700 border-emerald-100" : "bg-slate-50 text-slate-400 border-slate-100"}`} title={r.signed_at || ""}>
                        <CheckCircle2 className="w-3 h-3" /> {r.signed_at ? t.onb_signed : t.onb_signed_pending}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {r.has_contract && (
                      <button type="button" onClick={() => setPdfView({ r, lang: "" })}
                        className="px-2.5 py-2 rounded-lg text-xs font-bold text-sky-700 hover:bg-sky-50 border border-sky-200"
                        data-testid={`contract-view-${r.email}`}>{t.view}</button>
                    )}
                    {r.signed_at && r.langs && r.langs.length > 0 && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-1 rounded-lg bg-slate-50 border border-slate-200" data-testid={`contract-flags-${r.email}`}>
                        {r.langs.map((lg) => (
                          <button key={lg} type="button" title={lg.toUpperCase()}
                            onClick={() => setPdfView({ r, lang: lg })}
                            className="rounded-[3px] transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-sky-400"
                            data-testid={`contract-flag-${r.email}-${lg}`}>
                            <LangFlag lang={lg} size={22} />
                          </button>
                        ))}
                      </span>
                    )}
                    <button type="button" onClick={() => resetPassword(r)} disabled={isWorking}
                      className="inline-flex items-center gap-1 px-2.5 py-2 rounded-lg text-xs font-bold text-amber-700 hover:bg-amber-50 border border-amber-200 disabled:opacity-50"
                      data-testid={`contract-reset-pw-${r.email}`}>
                      <KeyRound className="w-3.5 h-3.5" /> {t.reset_pw}
                    </button>
                    {r.has_contract && r.sign_token && !r.signed_at && (
                      <button type="button" onClick={() => copyLink(r)}
                        className="inline-flex items-center gap-1 px-2.5 py-2 rounded-lg text-xs font-bold text-slate-600 hover:bg-slate-50 border border-slate-200"
                        data-testid={`contract-copylink-${r.email}`}>
                        <Link2 className="w-3.5 h-3.5" /> {t.copy_link}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setGenMember(r)}
                      disabled={isWorking}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-xs font-black shadow-sm disabled:bg-slate-300"
                      style={{ color: "#fff" }}
                      data-testid={`contract-generate-${r.email}`}
                    >
                      <FilePlus className="w-3.5 h-3.5" /> {t.generate}
                    </button>
                    <input
                      ref={(el) => { fileRefs.current[r.email] = el; }}
                      type="file" accept="application/pdf" hidden
                      onChange={(e) => onPick(r.email, e.target.files?.[0])}
                    />
                    <button
                      type="button"
                      onClick={() => fileRefs.current[r.email]?.click()}
                      disabled={isWorking}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-sky-600 hover:bg-sky-700 text-xs font-black shadow-sm disabled:bg-slate-300"
                      style={{ color: "#fff" }}
                      data-testid={`contract-assign-${r.email}`}
                    >
                      {isWorking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UploadCloud className="w-3.5 h-3.5" />}
                      {r.has_contract ? t.replace : t.assign}
                    </button>
                    {r.has_contract && (
                      <button
                        type="button"
                        onClick={() => clear(r.email)}
                        disabled={isWorking}
                        className="inline-flex items-center justify-center w-9 h-9 rounded-lg text-rose-600 hover:bg-rose-50 border border-rose-200"
                        data-testid={`contract-clear-${r.email}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {genMember && (
        <DoserneGenerateModal lang={lang} member={genMember.__new ? null : genMember} authHeader={authHeader}
          newEmployee={!!genMember.__new}
          onClose={() => setGenMember(null)} onDone={load} />
      )}
      {pdfView && (
        <PdfViewerModal
          url={`${API}/doserne/admin/contracts/pdf?email=${encodeURIComponent(pdfView.r.email)}&signed=${pdfView.r.signed_at ? 1 : 0}${pdfView.lang ? `&lang=${pdfView.lang}` : ""}`}
          authHeader={authHeader}
          filename={`Kontrakt_${(pdfView.r.full_name || "ansatt").replace(/\s+/g, "_")}${pdfView.lang ? `_${pdfView.lang.toUpperCase()}` : ""}.pdf`}
          title={`${t.view} · ${pdfView.r.full_name}${pdfView.lang ? ` · ${pdfView.lang.toUpperCase()}` : ""}`}
          labels={{ download: t.download_print || "Descarcă / Print", close: t.close || "Închide" }}
          onClose={() => setPdfView(null)}
        />
      )}
    </DoserneShell>
  );
}
